'use client';

import { type PropsWithChildren, useEffect, useState, type FC } from 'react';
import { XIcon, PlusIcon, FileText, Loader2Icon, AlertCircleIcon } from 'lucide-react';
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useAuiState,
  useAui,
} from '@assistant-ui/react';
import { useShallow } from 'zustand/shallow';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogTitle, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { isPastedText } from '@/lib/pasted-text';
import { PastedTextAttachment } from './pasted-text-attachment';
import { useChatCopy } from '@/lib/chat-i18n';
import { isBusinessAttachment } from '@/lib/business-file-attachments';
import { usePrivateChatImage } from '@/lib/private-chat-image';

const useFileSrc = (file: File | undefined) => {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return src;
};

const useAttachmentSrc = () => {
  const { file, src } = useAuiState(
    useShallow((s): { file?: File; src?: string } => {
      if (s.attachment.type !== 'image') return {};
      if (s.attachment.file) return { file: s.attachment.file };
      const src = s.attachment.content?.filter((c) => c.type === 'image')[0]?.image;
      if (!src) return {};
      return { src };
    }),
  );

  const preview = useFileSrc(file);
  const stored = usePrivateChatImage(src);
  return preview ?? stored;
};

type AttachmentPreviewProps = {
  src: string;
};

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const copy = useChatCopy();
  return (
    <img
      src={src}
      alt={copy.attachmentPreview}
      className={cn(
        'block h-auto max-h-[80vh] w-auto max-w-full object-contain',
        isLoaded
          ? 'aui-attachment-preview-image-loaded'
          : 'aui-attachment-preview-image-loading invisible',
      )}
      onLoad={() => setIsLoaded(true)}
    />
  );
};

const AttachmentPreviewDialog: FC<PropsWithChildren> = ({ children }) => {
  const src = useAttachmentSrc();
  const copy = useChatCopy();

  if (!src) return children;

  return (
    <Dialog>
      <DialogTrigger
        className="aui-attachment-preview-trigger hover:bg-accent/50 cursor-pointer transition-colors"
        asChild
      >
        {children}
      </DialogTrigger>
      <DialogContent className="aui-attachment-preview-dialog-content [&>button]:bg-foreground/60 [&_svg]:text-background [&>button]:hover:[&_svg]:text-destructive p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:p-1 [&>button]:opacity-100 [&>button]:ring-0!">
        <DialogTitle className="aui-sr-only sr-only">{copy.imageAttachmentPreview}</DialogTitle>
        <div className="aui-attachment-preview bg-background relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden">
          <AttachmentPreview src={src} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AttachmentThumb: FC = () => {
  const src = useAttachmentSrc();
  const copy = useChatCopy();

  return (
    <Avatar className="aui-attachment-tile-avatar h-full w-full rounded-none">
      <AvatarImage
        src={src}
        alt={copy.attachmentPreview}
        className="aui-attachment-tile-image object-cover"
      />
      <AvatarFallback>
        <FileText className="aui-attachment-tile-fallback-icon text-muted-foreground size-8" />
      </AvatarFallback>
    </Avatar>
  );
};

const AttachmentUI: FC = () => {
  const isPaste = useAuiState((state) => isPastedText(state.attachment));
  return isPaste ? <PastedTextAttachment /> : <FileAttachmentUI />;
};

const BusinessAttachmentLabel = ({
  name,
  uploadState,
}: {
  name: string;
  uploadState: 'uploading' | 'error' | undefined;
}) => {
  const Icon =
    uploadState === 'uploading'
      ? Loader2Icon
      : uploadState === 'error'
        ? AlertCircleIcon
        : FileText;
  return (
    <>
      <Icon
        aria-hidden="true"
        className={cn(
          'size-5 shrink-0 text-muted-foreground',
          uploadState === 'uploading' && 'aui-attachment-tile-uploading animate-spin',
          uploadState === 'error' && 'aui-attachment-tile-error text-destructive',
        )}
      />
      <span className="truncate text-sm">{name}</span>
    </>
  );
};

const FileAttachmentUI: FC = () => {
  const aui = useAui();
  const copy = useChatCopy();
  const isComposer = aui.attachment.source !== 'message';

  const isImage = useAuiState((s) => s.attachment.type === 'image');
  const businessName = useAuiState((s) =>
    isBusinessAttachment(s.attachment) ? s.attachment.name : null,
  );
  const typeLabel = useAuiState((s) => {
    const type = s.attachment.type;
    switch (type) {
      case 'image':
        return copy.image;
      case 'document':
        return copy.document;
      case 'file':
        return copy.file;
      default:
        return type;
    }
  });

  const uploadState = useAuiState((s) =>
    s.attachment.status.type === 'running'
      ? 'uploading'
      : s.attachment.status.type === 'incomplete' && s.attachment.status.reason === 'error'
        ? 'error'
        : undefined,
  );
  const isUploading = uploadState === 'uploading';
  const isError = uploadState === 'error';

  return (
    <Tooltip>
      <AttachmentPrimitive.Root
        className={cn(
          'aui-attachment-root relative',
          isImage && !isComposer && 'aui-attachment-root-message only:*:first:size-24',
        )}
      >
        <AttachmentPreviewDialog>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'aui-attachment-tile bg-muted relative size-14 cursor-pointer overflow-hidden rounded-[calc(var(--composer-radius)-var(--composer-padding))] border transition-opacity hover:opacity-75',
                businessName && 'flex w-56 max-w-full items-center gap-2 px-3 pr-6',
                isError && 'border-destructive',
              )}
              role="button"
              tabIndex={0}
              aria-label={`${businessName ?? `${typeLabel} ${copy.attachment}`}${
                isError ? `, ${copy.uploadFailed}` : isUploading ? `, ${copy.uploading}` : ''
              }`}
            >
              {businessName ? (
                <BusinessAttachmentLabel name={businessName} uploadState={uploadState} />
              ) : (
                <AttachmentThumb />
              )}
              {!businessName && isUploading && (
                <div
                  aria-hidden="true"
                  className="aui-attachment-tile-uploading bg-background/60 absolute inset-0 flex items-center justify-center backdrop-blur-[1px]"
                >
                  <Loader2Icon className="text-muted-foreground size-5 animate-spin" />
                </div>
              )}
              {!businessName && isError && (
                <div
                  aria-hidden="true"
                  className="aui-attachment-tile-error bg-destructive/10 absolute inset-0 flex items-center justify-center"
                >
                  <AlertCircleIcon className="text-destructive size-5" />
                </div>
              )}
            </div>
          </TooltipTrigger>
        </AttachmentPreviewDialog>
        {isComposer && <AttachmentRemove />}
      </AttachmentPrimitive.Root>
      <TooltipContent side="top">
        <AttachmentPrimitive.Name />
      </TooltipContent>
    </Tooltip>
  );
};

const AttachmentRemove: FC = () => {
  const copy = useChatCopy();
  return (
    <AttachmentPrimitive.Remove asChild>
      <TooltipIconButton
        tooltip={copy.removeFile}
        className="aui-attachment-tile-remove text-muted-foreground hover:[&_svg]:text-destructive absolute end-1.5 top-1.5 size-3.5 rounded-full bg-white opacity-100 shadow-sm hover:bg-white! [&_svg]:text-black"
        side="top"
      >
        <XIcon className="aui-attachment-remove-icon size-3 dark:stroke-[2.5px]" />
      </TooltipIconButton>
    </AttachmentPrimitive.Remove>
  );
};

export const UserMessageAttachments: FC = () => {
  return (
    <div className="aui-user-message-attachments-end col-span-full col-start-1 row-start-1 flex w-full flex-row flex-wrap justify-end gap-2">
      <MessagePrimitive.Attachments>{() => <AttachmentUI />}</MessagePrimitive.Attachments>
    </div>
  );
};

export const ComposerAttachments: FC = () => {
  return (
    <div className="aui-composer-attachments flex w-full flex-row flex-wrap items-center gap-2 empty:hidden">
      <ComposerPrimitive.Attachments>{() => <AttachmentUI />}</ComposerPrimitive.Attachments>
    </div>
  );
};

export const ComposerAddAttachment: FC = () => {
  const copy = useChatCopy();
  return (
    <ComposerPrimitive.AddAttachment asChild>
      <TooltipIconButton
        tooltip={copy.addAttachment}
        side="bottom"
        variant="ghost"
        size="icon"
        className="aui-composer-add-attachment hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30 size-11 rounded-full p-1 text-xs font-semibold md:size-7"
        aria-label={copy.addAttachment}
      >
        <PlusIcon className="aui-attachment-add-icon size-4.5 stroke-[1.5px]" />
      </TooltipIconButton>
    </ComposerPrimitive.AddAttachment>
  );
};
